import { useState, useRef, useCallback, useEffect } from "react";
import { X, ZoomIn } from "lucide-react";

/**
 * ZoomableImage
 *
 * A lightweight two-mode image viewer used for the posted-kit preview
 * (and reusable anywhere a marketing/product image needs a closer look).
 *
 *   1. **Hover / drag magnifier** — desktop only. When the mouse enters
 *      the image, a circular lens (default ~140px) follows the pointer
 *      showing a 2.2x zoomed crop of the area beneath it. This mimics
 *      the "drag across to magnify" gesture common on e-commerce sites.
 *      Skipped on touch devices so the tap goes straight to lightbox.
 *
 *   2. **Click / tap lightbox** — opens a full-screen dark overlay
 *      with the un-cropped image (object-contain), an accessible close
 *      button, ESC-to-close, backdrop click to close, and an optional
 *      caption underneath.
 *
 * The container preserves the caller's sizing (`className` passthrough
 * on the wrapper) and never distorts the parent layout — the lens
 * renders inside the container via absolute positioning.
 */
export function ZoomableImage({
  src,
  alt = "",
  caption = "",
  className = "",
  imgClassName = "w-full h-full object-cover",
  zoom = 2.2,
  lensSize = 140,
  testId = "zoomable-image",
}) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [lensVisible, setLensVisible] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [bgSize, setBgSize] = useState({ w: 0, h: 0 });
  const [open, setOpen] = useState(false);

  // Recompute background size on load/resize so the lens shows the
  // right zoom regardless of the container's rendered dimensions.
  const recomputeBg = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setBgSize({ w: rect.width * zoom, h: rect.height * zoom });
  }, [zoom]);

  useEffect(() => {
    recomputeBg();
    window.addEventListener("resize", recomputeBg);
    return () => window.removeEventListener("resize", recomputeBg);
  }, [recomputeBg]);

  const handleMove = useCallback((e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      setLensVisible(false);
      return;
    }
    setLensPos({ x, y });
    setLensVisible(true);
  }, []);

  // ESC to close lightbox
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    // Lock page scroll while the lightbox is open so mobile pinch-zoom
    // works cleanly and background content doesn't shift.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const bgX = bgSize.w
    ? -((lensPos.x / (wrapRef.current?.offsetWidth || 1)) * bgSize.w - lensSize / 2)
    : 0;
  const bgY = bgSize.h
    ? -((lensPos.y / (wrapRef.current?.offsetHeight || 1)) * bgSize.h - lensSize / 2)
    : 0;

  return (
    <>
      <div
        ref={wrapRef}
        className={`relative group cursor-zoom-in select-none ${className}`}
        onMouseEnter={recomputeBg}
        onMouseMove={handleMove}
        onMouseLeave={() => setLensVisible(false)}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
        aria-label={caption ? `Expand image: ${caption}` : "Expand image"}
        data-testid={testId}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={imgClassName}
          onLoad={recomputeBg}
          draggable={false}
        />
        {/* Corner hint — nudges the customer that the image is interactive */}
        <div
          className="pointer-events-none absolute top-1.5 right-1.5 bg-black/55 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-hidden="true"
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </div>
        {/* Magnifier lens (desktop hover only) */}
        {lensVisible && bgSize.w > 0 && (
          <div
            className="pointer-events-none absolute rounded-full border-2 border-white shadow-xl hidden md:block"
            style={{
              width: lensSize,
              height: lensSize,
              left: lensPos.x - lensSize / 2,
              top: lensPos.y - lensSize / 2,
              backgroundImage: `url(${src})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: `${bgSize.w}px ${bgSize.h}px`,
              backgroundPosition: `${bgX}px ${bgY}px`,
              boxShadow: "0 0 0 3px rgba(6,182,212,0.35), 0 10px 30px rgba(0,0,0,0.35)",
            }}
            data-testid={`${testId}-lens`}
          />
        )}
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-4 sm:p-8"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={caption || "Expanded image"}
          data-testid={`${testId}-lightbox`}
        >
          <button
            type="button"
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/25 text-white rounded-full p-2 transition-colors"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            aria-label="Close expanded image"
            data-testid={`${testId}-lightbox-close`}
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
          {caption && (
            <div className="mt-4 max-w-2xl text-center text-sm text-white/85 leading-relaxed">
              {caption}
            </div>
          )}
        </div>
      )}
    </>
  );
}
